import React, { useState, useEffect, useMemo } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  Switch,
  CircularProgress,
  Alert,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VideocamIcon from "@mui/icons-material/Videocam";
import LinkIcon from "@mui/icons-material/Link";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { submitSessionRequest } from "../api/apiClient";
import { fetchRooms, fetchSites } from "../api/rooms";

const T = {
  surface2: "var(--color-surface-2)",
  line:     "var(--color-hairline-cloud)",
  line2:    "var(--color-surface-press-light)",
  surface3: "var(--color-surface-press-light)",
  ink:      "var(--color-ink)",
  ink2:     "var(--color-ink-2)",
  ink3:     "var(--color-ink-3)",
  ink4:     "var(--color-ink-4)",
  danger:   "#B42318",
};

function makeCtrlSx(tPrimary) {
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      bgcolor: T.surface2,
      "& fieldset": { borderWidth: "1.5px", borderColor: T.line },
      "&:hover fieldset": { borderColor: "var(--color-hairline-cool)" },
      "&.Mui-focused fieldset": { borderColor: tPrimary, borderWidth: "1.5px" },
    },
    "& .MuiInputBase-input": { py: "10px", fontSize: 14, fontWeight: 500, color: T.ink },
    "& .MuiInputAdornment-root": { color: T.ink4 },
  };
}

function FieldLabel({ children, required }) {
  return (
    <Typography sx={{
      display: "block", fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em",
      textTransform: "uppercase", color: T.ink3, mb: "7px",
    }}>
      {children}
      {required && <Box component="span" sx={{ color: T.danger, ml: "2px" }}>*</Box>}
    </Typography>
  );
}

function TypeCard({ selected, onClick, icon, label, sub, tPrimary, tPrimary10 }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex", flexDirection: "column", alignItems: "center", gap: "8px",
        p: "16px 12px 14px", borderRadius: "14px", position: "relative",
        border: `2px solid ${selected ? tPrimary : T.line}`,
        bgcolor: selected ? tPrimary10 : T.surface2,
        cursor: "pointer", transition: "all .16s",
        "&:hover": { borderColor: selected ? tPrimary : "var(--color-hairline-cool)", bgcolor: selected ? tPrimary10 : "#fff" },
      }}
    >
      <Box sx={{
        position: "absolute", top: 9, right: 9, width: 18, height: 18, borderRadius: "50%",
        border: `2px solid ${selected ? tPrimary : T.line}`,
        bgcolor: selected ? tPrimary : "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all .16s",
      }}>
        {selected && (
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </Box>
      <Box sx={{
        width: 40, height: 40, borderRadius: "11px",
        display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: selected ? tPrimary : T.surface3,
        color: selected ? "#fff" : T.ink3,
        transition: "all .16s", flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: selected ? tPrimary : T.ink3, transition: "color .16s", fontFamily: "var(--font-display)" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "11.5px", color: selected ? alpha(tPrimary, 0.6) : T.ink4, textAlign: "center", lineHeight: 1.4 }}>
        {sub}
      </Typography>
    </Box>
  );
}

const EMPTY = {
  date: "",
  startTime: "08:00",
  endTime: "12:00",
  sessionType: "PHYSICAL",
  location: "",
  trackLocation: true,
  meetingLink: "",
  capacity: 30,
};

export default function SessionRequestDialog({ open, onClose, onSuccess, courseId, courseName, instructorId }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimaryDk = theme.palette.primary.dark;
  const tPrimary10 = alpha(tPrimary, 0.06);
  const CTRL_SX = makeCtrlSx(tPrimary);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [rooms, setRooms] = useState([]);
  const [sites, setSites] = useState([]);

  useEffect(() => {
    if (!open) return;
    Promise.all([fetchRooms(), fetchSites()])
      .then(([r, s]) => { setRooms(r); setSites(s); })
      .catch(() => {});
  }, [open]);

  const sitesById = useMemo(() => Object.fromEntries(sites.map((s) => [s.id, s])), [sites]);

  function handleClose() {
    setForm(EMPTY);
    setError("");
    onClose();
  }

  async function handleSubmit() {
    setError("");
    if (!form.date) { setError("Date is required."); return; }
    if (form.sessionType === "PHYSICAL" && !form.location) {
      setError("Location is required for in-person sessions."); return;
    }
    if (form.sessionType === "VIRTUAL" && !form.meetingLink.trim()) {
      setError("Meeting link is required for virtual sessions."); return;
    }
    setSaving(true);
    try {
      await submitSessionRequest(courseId, {
        instructorId,
        sessionDate: form.date,
        startTime: form.startTime || undefined,
        endTime: form.endTime || undefined,
        sessionType: form.sessionType,
        location: form.location || undefined,
        meetingLink: form.meetingLink || undefined,
        trackLocation: form.trackLocation,
        capacity: form.capacity,
      });
      setForm(EMPTY);
      setError("");
      onSuccess();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to submit request.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: "100%", maxWidth: 480, borderRadius: "22px",
          boxShadow: "0 28px 72px -18px rgba(20,24,40,.36), 0 0 0 1px rgba(20,24,40,.06)",
          overflow: "hidden", m: 2,
        },
      }}
    >
      {/* HEADER */}
      <Box sx={{
        display: "flex", alignItems: "flex-start", gap: "14px",
        p: "22px 24px 18px", borderBottom: `1px solid ${T.line2}`,
      }}>
        <Box sx={{
          width: 42, height: 42, borderRadius: "12px", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: tPrimary10, color: tPrimary,
        }}>
          <CalendarTodayIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 800, letterSpacing: "-.025em", color: T.ink, lineHeight: 1.2, mb: "3px", fontFamily: "var(--font-display)" }}>
            Request New Session
          </Typography>
          <Typography sx={{ fontSize: 13, color: T.ink3 }}>
            {courseName
              ? <><strong style={{ color: T.ink2 }}>{courseName}</strong> — pending coordinator approval</>
              : "Pending coordinator approval"}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            width: 32, height: 32, borderRadius: "9px",
            border: `1px solid ${T.line}`, bgcolor: "#fff", color: T.ink3,
            flexShrink: 0, mt: "-2px",
            "&:hover": { bgcolor: T.surface3, color: T.ink },
          }}
        >
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      {/* BODY */}
      <Box sx={{ p: "20px 24px", display: "flex", flexDirection: "column", gap: "18px", overflowY: "auto", maxHeight: "calc(100vh - 240px)" }}>
        {error && <Alert severity="error" sx={{ borderRadius: "10px" }}>{error}</Alert>}

        {/* Date */}
        <Box>
          <FieldLabel required>Date</FieldLabel>
          <TextField
            fullWidth
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarTodayIcon sx={{ fontSize: 15, color: T.ink4 }} />
                </InputAdornment>
              ),
            }}
            sx={CTRL_SX}
          />
        </Box>

        {/* Start + End Time */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
          <Box>
            <FieldLabel required>Start Time</FieldLabel>
            <TextField
              fullWidth
              type="time"
              value={form.startTime}
              onChange={(e) => setForm({ ...form, startTime: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccessTimeIcon sx={{ fontSize: 15, color: T.ink4 }} />
                  </InputAdornment>
                ),
              }}
              sx={CTRL_SX}
            />
          </Box>
          <Box>
            <FieldLabel required>End Time</FieldLabel>
            <TextField
              fullWidth
              type="time"
              value={form.endTime}
              onChange={(e) => setForm({ ...form, endTime: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccessTimeIcon sx={{ fontSize: 15, color: T.ink4 }} />
                  </InputAdornment>
                ),
              }}
              sx={CTRL_SX}
            />
          </Box>
        </Box>

        {/* Session Type cards */}
        <Box>
          <FieldLabel required>Session Type</FieldLabel>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <TypeCard
              selected={form.sessionType === "PHYSICAL"}
              onClick={() => setForm({ ...form, sessionType: "PHYSICAL", meetingLink: "" })}
              icon={<LocationOnIcon sx={{ fontSize: 20 }} />}
              label="In-Person"
              sub="Physical training location"
              tPrimary={tPrimary}
              tPrimary10={tPrimary10}
            />
            <TypeCard
              selected={form.sessionType === "VIRTUAL"}
              onClick={() => setForm({ ...form, sessionType: "VIRTUAL", location: "" })}
              icon={<VideocamIcon sx={{ fontSize: 20 }} />}
              label="Virtual"
              sub="Online meeting link"
              tPrimary={tPrimary}
              tPrimary10={tPrimary10}
            />
          </Box>
        </Box>

        {/* Location dropdown (in-person) */}
        {form.sessionType === "PHYSICAL" && (
          <Box>
            <FieldLabel required>Location</FieldLabel>
            <Select
              fullWidth
              displayEmpty
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              sx={{
                height: 44, borderRadius: "12px", bgcolor: T.surface2,
                fontSize: 14, fontWeight: form.location ? 500 : 400,
                color: form.location ? T.ink : T.ink4,
                "& .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: T.line },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-hairline-cool)" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: tPrimary, borderWidth: "1.5px" },
                "& .MuiSelect-select": { display: "flex", alignItems: "center", py: 0 },
              }}
            >
              <MenuItem value="" disabled sx={{ color: T.ink4 }}>Select a location</MenuItem>
              {rooms.map((r) => {
                const site = sitesById[r.siteId];
                const label = site ? `${r.name} — ${site.name}` : r.name;
                return (
                  <MenuItem key={r.id} value={label}>{label}</MenuItem>
                );
              })}
              <MenuItem value="Other">Other (specify in notes)</MenuItem>
            </Select>
          </Box>
        )}

        {/* Meeting link (virtual) */}
        {form.sessionType === "VIRTUAL" && (
          <Box>
            <FieldLabel required>Meeting Link</FieldLabel>
            <TextField
              fullWidth
              type="url"
              value={form.meetingLink}
              onChange={(e) => setForm({ ...form, meetingLink: e.target.value })}
              placeholder="https://zoom.us/j/..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon sx={{ fontSize: 15, color: T.ink4 }} />
                  </InputAdornment>
                ),
              }}
              sx={CTRL_SX}
            />
          </Box>
        )}

        {/* Track Location toggle */}
        <Box sx={{
          display: "flex", alignItems: "center", gap: "14px",
          bgcolor: T.surface2, border: `1.5px solid ${T.line2}`, borderRadius: "12px", p: "14px 16px",
        }}>
          <Box sx={{
            width: 34, height: 34, borderRadius: "9px", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: tPrimary10, color: tPrimary,
          }}>
            <LocationOnIcon sx={{ fontSize: 17 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: T.ink }}>
              Track student location on check-in
            </Typography>
            <Typography sx={{ fontSize: 12, color: T.ink3, mt: "1px" }}>
              Verifies students are physically on-site
            </Typography>
          </Box>
          <Switch
            checked={form.trackLocation}
            onChange={(e) => setForm({ ...form, trackLocation: e.target.checked })}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: tPrimary },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: tPrimary },
            }}
          />
        </Box>

        {/* Capacity stepper */}
        <Box>
          <FieldLabel required>Session Capacity</FieldLabel>
          <Box sx={{
            display: "flex", alignItems: "center",
            border: `1.5px solid ${T.line}`, borderRadius: "12px", overflow: "hidden",
            bgcolor: T.surface2,
          }}>
            <Box
              component="button"
              type="button"
              onClick={() => setForm(f => ({ ...f, capacity: Math.max(1, f.capacity - 5) }))}
              sx={{
                width: 44, height: 44, border: 0, bgcolor: "transparent",
                color: T.ink3, fontSize: 20, fontWeight: 300, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, transition: "all .13s", fontFamily: "inherit",
                "&:hover": { bgcolor: T.surface3, color: T.ink },
              }}
            >−</Box>
            <Box sx={{ width: "1px", bgcolor: T.line2, height: 26, flexShrink: 0 }} />
            <Box
              component="input"
              type="number"
              value={form.capacity}
              onChange={(e) => setForm(f => ({ ...f, capacity: Math.max(1, parseInt(e.target.value, 10) || 1) }))}
              sx={{
                flex: 1, textAlign: "center", fontSize: 16, fontWeight: 800,
                color: T.ink, letterSpacing: "-.02em", border: 0, bgcolor: "transparent",
                outline: "none", fontFamily: "inherit", height: 44,
                "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": { WebkitAppearance: "none" },
              }}
            />
            <Box sx={{ width: "1px", bgcolor: T.line2, height: 26, flexShrink: 0 }} />
            <Box
              component="button"
              type="button"
              onClick={() => setForm(f => ({ ...f, capacity: Math.min(200, f.capacity + 5) }))}
              sx={{
                width: 44, height: 44, border: 0, bgcolor: "transparent",
                color: T.ink3, fontSize: 20, fontWeight: 300, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, transition: "all .13s", fontFamily: "inherit",
                "&:hover": { bgcolor: T.surface3, color: T.ink },
              }}
            >+</Box>
          </Box>
        </Box>
      </Box>

      {/* FOOTER */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "9px",
        p: "14px 24px 18px", borderTop: `1px solid ${T.line2}`, bgcolor: T.surface2,
      }}>
        <Button
          onClick={handleClose}
          disabled={saving}
          sx={{
            height: 44, px: "20px", borderRadius: "12px",
            border: `1.5px solid ${T.line}`, bgcolor: "#fff",
            fontSize: 14, fontWeight: 600, color: T.ink2, textTransform: "none",
            "&:hover": { bgcolor: T.surface3, color: T.ink, borderColor: "var(--color-hairline-cool)" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving}
          endIcon={saving
            ? <CircularProgress size={14} sx={{ color: "#fff" }} />
            : <SendIcon sx={{ fontSize: 15, transition: "transform .15s" }} />
          }
          sx={{
            height: 44, px: "22px", borderRadius: "12px",
            bgcolor: tPrimary, color: "#fff",
            fontSize: 14, fontWeight: 700, textTransform: "none",
            boxShadow: `0 6px 16px -6px ${alpha(tPrimary, 0.52)}`,
            "&:hover": { bgcolor: tPrimaryDk, boxShadow: `0 10px 22px -8px ${alpha(tPrimary, 0.6)}`, "& .MuiButton-endIcon svg": { transform: "translateX(3px)" } },
            "&:active": { transform: "translateY(1px)" },
            "&.Mui-disabled": { bgcolor: tPrimary, opacity: 0.55, color: "#fff" },
          }}
        >
          Submit for Approval
        </Button>
      </Box>
    </Dialog>
  );
}
